import type {
    Dispatch,
    SyntheticEvent,
    SetStateAction,
} from 'react'
import { useAtomValue } from "jotai"
import InputAdornment from '@mui/material/InputAdornment'
import GiteRoundedIcon from '@mui/icons-material/GiteRounded'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import Autocomplete from "@mui/material/Autocomplete"
import parse from 'autosuggest-highlight/parse'
import match from 'autosuggest-highlight/match'
import TextField from "@mui/material/TextField"

import { selectedMunicipalityIdAtom } from "@/state"
import { useCollectionPoints } from "@/hooks/api/use-collection-point"
import type { CollectionPointListItem } from "@/types"

export default function CollectionPointFilter({
    selectedId,
    setSelectedId,
    label,
    handleResetPage,
} : {
    selectedId: number | null,
    setSelectedId: Dispatch<SetStateAction<number | null>>,
    label?: string,
    handleResetPage?: () => void,
}) {

    const municipalityId = useAtomValue(selectedMunicipalityIdAtom)

    const { data, error } = useCollectionPoints(municipalityId)

    const getOptionLabel = (option: CollectionPointListItem) => (
        `${option.address.street ? option.address.street : '-'} ${option.address.street_number ? option.address.street_number : '-'} (${option.code})`
    )

    const options = data ? data.sort((a, b) => getOptionLabel(a).localeCompare(getOptionLabel(b), 'cs', { numeric: true })) : []

    return (
        <Autocomplete
            value={
                selectedId
                    ? options.find((option) => {
                        return selectedId === option.id
                    }) ?? null
                    : null
            }
            getOptionLabel={(option) => {
                return getOptionLabel(option)
            }}
            getOptionKey={(option) => {
                return option.id
            }}
            renderOption={(props, option, { inputValue }) => {
                const matches = match(getOptionLabel(option), inputValue)
                const parts = parse(getOptionLabel(option), matches)

                const text = (parts.map((part, index) => (
                    <span
                        key={index}
                        style={{
                            fontWeight: part.highlight ? 700 : 400,
                        }}
                    >
                        {part.text}
                    </span>
                )))

                return (
                    <li {...props} key={props.key}>
                        <div>
                            {text}
                        </div>
                    </li>
                );
            }}
            onChange={(_event: SyntheticEvent, newValue) => {
                if (handleResetPage) {
                    handleResetPage()
                }
                setSelectedId(newValue ? newValue.id : null)
            }}
            id="collectionPoint-filter"
            options={options}
            noOptionsText="Žádná svozová místa nenalezena"
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={!label ? "Svozové místo" : undefined}
                    label={label}
                    size={label ? "medium" : "small"}
                    inputProps={{
                        ...params.inputProps,
                        // disable autocomplete and autofill
                        autoComplete: "off",
                    }}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <InputAdornment position="start" sx={{ paddingLeft: 1}}>
                                {error ?
                                    <ErrorOutlineIcon sx={{ color: 'error.light' }} />
                                    :
                                    <GiteRoundedIcon sx={{ color: label ? 'customGrey.secondaryText' : 'customGrey.selectorIcon' }} />
                                }
                            </InputAdornment>
                        ),
                      }}
                    autoComplete="off"
                    sx={{
                        minWidth: 250,
                    }}
                />
            )}
        />
    )
}
